import { services } from '@config/services';
import type {
  MerchantPlugin,
  MerchantPluginDetail,
  MerchantPluginsListResponse,
  UpdateMerchantPluginPayload,
} from '../types';
import { integrationApi, unwrapArray } from './_base';

/**
 * Merchant-scoped plugin endpoints (`/v1/merchantPlugin*`). These are
 * the "My Plugins" / connect / disconnect / variable-overrides path.
 * Plugin-record CRUD is in `./plugins.ts`.
 */

export interface MerchantPluginListArgs {
  merchantId: string;
  page?: number;
  pageSize?: number;
  isPublished?: boolean;
}

export interface ConnectPluginArgs {
  pluginId: string;
  pluginVersionId?: string;
  merchantId: string;
  variableOverrides: Record<string, string>;
}

export interface TestConnectionArgs {
  merchantPluginId: string;
  /** What the merchant has typed but not saved. Omit to test what is stored. */
  variableOverrides?: Record<string, string>;
}

/** Body of a 200 from `POST merchantPlugin/{id}/testConnection`. */
export interface TestConnectionResult {
  /** Whether the provider accepted the credentials. */
  success: boolean;
  /** What to show the merchant. Carries the provider's own words, never a credential. */
  message: string;
}

/** Raw shapes the merchant-plugins endpoint may return — a bare array or one
 * of several envelope variants. The integration service wraps the rows under
 * `merchantPlugins` and carries `allowedPluginTypes` alongside them. */
export type RawMerchantPluginsResponse =
  | MerchantPlugin[]
  | {
      plugins?: MerchantPlugin[];
      merchantPlugins?: MerchantPlugin[];
      data?: MerchantPlugin[];
      allowedPluginTypes?: string[];
      total?: number;
    };

/**
 * Normalize the merchant-plugins response into the shape the UI consumes:
 * `{ plugins, total, allowedPluginTypes }`. `allowedPluginTypes`
 * gates the per-card Edit/Disconnect menu and was previously dropped by the
 * bare-array unwrap. Fail-open: when the field is absent → `[]`, which
 * PluginCard reads as "no restriction" (the backend still 403s a disallowed
 * mutation). Exported so the response-envelope contract is unit-tested.
 */
export function transformMerchantPluginsResponse(
  response: RawMerchantPluginsResponse,
): MerchantPluginsListResponse {
  const plugins = unwrapArray(response);
  const allowedPluginTypes = Array.isArray(response) ? [] : response.allowedPluginTypes ?? [];
  const total = Array.isArray(response) ? plugins.length : response.total ?? plugins.length;
  return { plugins, total, allowedPluginTypes };
}

/**
 * Request builder, exported so the wire shape is asserted directly rather than
 * inferred from a mounted component (the house pattern — see
 * `versionManagementRequestBuilders`).
 */
export const testConnectionRequestBuilders = {
  testConnection: ({ merchantPluginId, variableOverrides }: TestConnectionArgs) => ({
    url: `${services.integration}/v1/merchantPlugin/${encodeURIComponent(merchantPluginId)}/testConnection`,
    method: 'POST',
    // Omitted rather than sent empty: the service reads a missing body as
    // "test what is stored", and an empty override map means the same thing to
    // it, but sending one would claim the merchant had cleared every variable.
    body: variableOverrides ? { variableOverrides } : undefined,
  }),
};

/**
 * Normalizes a Test Connection answer.
 *
 * The service has only ever shipped the bare object, but a truncated or empty
 * body would otherwise reach the button as `success: undefined`, which renders
 * as a refusal — telling the merchant to fix a field that is fine.
 */
export function transformTestConnectionResponse(
  response: Partial<TestConnectionResult> | null,
): TestConnectionResult {
  return {
    success: response?.success === true,
    message: response?.message ?? '',
  };
}

const merchantPluginsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Per-merchant connected plugins ("My Plugins" tab). Returns plugins
     * the merchant has connected, regardless of setup state — the row
     * carries `status` ('Active' / 'SetupIncomplete' / etc.) so the UI can
     * render the badge.
     */
    getMerchantPlugins: builder.query<MerchantPluginsListResponse, MerchantPluginListArgs>({
      query: ({ merchantId }) => ({
        url: `${services.integration}/v1/merchantPlugin/merchant/${encodeURIComponent(merchantId)}`,
        method: 'GET',
      }),
      // Normalize to { plugins, total, allowedPluginTypes } — see
      // transformMerchantPluginsResponse. `allowedPluginTypes`
      // rides this envelope and gates the per-card Actions menu in PluginCard.
      transformResponse: transformMerchantPluginsResponse,
      providesTags: (result) =>
        result?.plugins
          ? [
              ...result.plugins.map((p) => ({ type: 'MerchantPlugins' as const, id: p.merchantPluginId ?? p.id })),
              { type: 'MerchantPlugins' as const, id: 'LIST' },
            ]
          : [{ type: 'MerchantPlugins' as const, id: 'LIST' }],
    }),
    /**
     * Connect a plugin to a merchant. Server returns the new merchantPlugin
     * row; we invalidate the MerchantPlugins tag so the My Plugins tab
     * refetches and the "Connect" button on the marketplace tile flips to
     * "View" on the next render.
     */
    connectPlugin: builder.mutation<MerchantPlugin, ConnectPluginArgs>({
      query: (body) => ({
        url: `${services.integration}/v1/merchantPlugin`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'MerchantPlugins', id: 'LIST' },
        { type: 'Plugins', id: 'LIST' },
      ],
    }),
    /**
     * Disconnect a merchant plugin. Reuses the same invalidation contract
     * as connect so the two tabs stay in lockstep without separate refetch
     * dispatches.
     */
    disconnectPlugin: builder.mutation<void, string>({
      query: (merchantPluginId) => ({
        url: `${services.integration}/v1/merchantPlugin/${encodeURIComponent(merchantPluginId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'MerchantPlugins', id: 'LIST' },
        { type: 'Plugins', id: 'LIST' },
      ],
    }),
    /**
     * Full detail row for a connected plugin. Drives the ConnectionDetails
     * page (variables, overrides, enable flag, basicInfo wrapper).
     */
    getMerchantPluginDetail: builder.query<MerchantPluginDetail, string>({
      query: (merchantPluginId) => ({
        url: `${services.integration}/v1/merchantPlugin/${encodeURIComponent(merchantPluginId)}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, merchantPluginId) => [
        { type: 'MerchantPluginDetail', id: merchantPluginId },
      ],
    }),
    /**
     * Save edits to a connected plugin — flips enable or persists new
     * variable overrides. Tag-invalidates the detail row and the My
     * Plugins list so both refetch and stay in sync.
     */
    updateMerchantPlugin: builder.mutation<
      MerchantPluginDetail,
      { merchantPluginId: string; body: UpdateMerchantPluginPayload }
    >({
      query: ({ merchantPluginId, body }) => ({
        url: `${services.integration}/v1/merchantPlugin/${encodeURIComponent(merchantPluginId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [
        { type: 'MerchantPluginDetail', id: merchantPluginId },
        { type: 'MerchantPlugins', id: 'LIST' },
      ],
    }),
    /**
     * Ask the tax provider whether it accepts these credentials, without
     * saving them. Wire shape, verified against integration-service
     * `MerchantPluginController.TestConnection` on origin/main:
     *
     *   POST /integration/v1/merchantPlugin/{merchantPluginId}/testConnection
     *   body: { "variableOverrides": { "<key>": "<value>" } }   (optional)
     *   200:  { "success": true|false, "message": "..." }
     *   502:  the credentials were never judged (timeout, 5xx, broken snippet)
     *
     * `variableOverrides` carries what the merchant has typed but not saved, so
     * the test answers for the form in front of them rather than for the stored
     * connection. Omitting it tests what is stored.
     *
     * No `invalidatesTags`: the handler writes nothing, so nothing cached went
     * stale. `success: false` is a 200 — the provider judged the credentials
     * and refused them, which is the answer the button asked for, not an error.
     */
    testMerchantPluginConnection: builder.mutation<
      TestConnectionResult,
      TestConnectionArgs
    >({
      query: (args) => testConnectionRequestBuilders.testConnection(args),
      transformResponse: transformTestConnectionResponse,
    }),
  }),
});

export const {
  useGetMerchantPluginsQuery,
  useConnectPluginMutation,
  useDisconnectPluginMutation,
  useGetMerchantPluginDetailQuery,
  useUpdateMerchantPluginMutation,
  useTestMerchantPluginConnectionMutation,
} = merchantPluginsApi;
